import { Component, type ErrorInfo, type ReactNode, Suspense, useMemo } from 'react';
import { PlanUsageOverview } from '@gonfalon/billing/components';

import { Card, ErrorCallout } from '../../shared';
import { Box, Flex } from '../../shared/components';
import { useSettings } from '../../shared/hooks/useSettings';
import { useAccountSubscription } from '../hooks/useAccountSubscription';
import { type Account } from '../model';

import { AccountContextProvider } from './AccountContextProvider';

type PlanUsageTabProps = {
  account: Account;
};

type ErrorBoundaryState = {
  hasError: boolean;
};

class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Plan Usage Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorCallout>
          <strong>Failed to load plan usage data</strong>
          <p>Plan usage data could not be loaded. Please try refreshing the page.</p>
        </ErrorCallout>
      );
    }

    return this.props.children;
  }
}

export function PlanUsageTab({ account }: PlanUsageTabProps) {
  const { data: subscription, isLoading, error } = useAccountSubscription(account);
  const { vitalstatistixDataExportObservability } = useSettings();

  // Create custom navigation function for vitalstatistix routing
  const detailsRedirectFn = useMemo(
    () =>
      ({ metric }: { metric: string }) => ({
        pathname: `/accounts/${account._id}/plan-usage/details/${metric}`,
        search: '',
        hash: '',
      }),
    [account._id],
  );

  if (isLoading) {
    return (
      <div className="PlanUsageTab">
        <Flex>
          <Box width={1} p={2}>
            <Card>
              <div>Loading plan usage data...</div>
            </Card>
          </Box>
        </Flex>
      </div>
    );
  }

  if (error) {
    return (
      <div className="PlanUsageTab">
        <Flex>
          <Box width={1} p={2}>
            <Card>
              <ErrorCallout>
                <strong>Failed to load plan usage data</strong>
                <p>Plan usage data could not be loaded. Please try refreshing the page.</p>
              </ErrorCallout>
            </Card>
          </Box>
        </Flex>
      </div>
    );
  }

  return (
    <div className="PlanUsageTab">
      <Flex>
        <Box width={1} p={2}>
          <Card>
            <Suspense fallback={<div>Loading plan usage data...</div>}>
              <ErrorBoundary>
                <AccountContextProvider
                  key={String(vitalstatistixDataExportObservability)}
                  account={account}
                  subscription={subscription || undefined}
                >
                  <PlanUsageOverview detailsRedirectFn={detailsRedirectFn} />
                </AccountContextProvider>
              </ErrorBoundary>
            </Suspense>
          </Card>
        </Box>
      </Flex>
    </div>
  );
}
